import { isLoading } from "apollo/utils";
import clsx from "clsx";
import { QUERY_PROGRESS_NOT_STARTED } from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { getStaticAsset } from "core/model/config";
import { formatUnixDate } from "core/model/utils/dates";
import { FileState, Patient, QueryProgress } from "core/types";
import {
  Banner,
  Button,
  Card,
  CardBody,
  Chip,
  ChipProps,
  Image,
  Spinner,
  Tabs,
} from "ds/ui";
import { PdfViewer, PdfViewerFile } from "dsl/atoms/PDFViewer";
import { useCreateFileUrl } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  DotIcon,
  FileTextIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "translations";

const Header = ({
  files,
  onBack,
  onSelectionChange,
  selectedFile,
  sourceDocuments,
}: {
  sourceDocuments?: { file_id: FileState["id"] }[];
  selectedFile: PdfViewerFile | undefined;
  onBack: () => void;
  onSelectionChange: (fileId: string) => void;
  files: { name: string; file: FileState }[];
}) => {
  const translations = useTranslations();
  const locale = useLocale();

  if (!selectedFile) {
    return null;
  }

  if (!sourceDocuments?.length || sourceDocuments.length === 1) {
    return (
      <div className="flex w-full items-center gap-4">
        <Button
          aria-label={translations.actions.back}
          color="primary"
          id="back"
          isIconOnly
          startContent={<ArrowLeftIcon />}
          variant="ghost"
          onPress={onBack}
        />
        <div className="flex flex-col">
          <h3 className="text-heading-sm font-bold">
            {selectedFile.isDeleted
              ? translations.extractDocs.splitView.sourceInformation
                  .deletedFileName
              : selectedFile.fileName}
          </h3>
          {!!selectedFile.uploadedAt && (
            <p className="text-body-md text-gray-600">
              {translations.extractDocs.splitView.sourcesTab.uploadedFileDateDetail(
                {
                  date: formatUnixDate(selectedFile.uploadedAt, locale),
                },
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <Banner
        color="primary"
        message={
          translations.extractDocs.splitView.sourcesTab
            .multipleSourcesUsedInformation
        }
        withClose
      />
      <div className="flex w-full items-center justify-between gap-4">
        <Button
          aria-label={translations.actions.back}
          color="primary"
          id="back"
          isIconOnly
          startContent={<ArrowLeftIcon />}
          variant="ghost"
          onPress={onBack}
        />
        <Tabs
          items={sourceDocuments.map((sourceDocument, index) => ({
            key: String(sourceDocument.file_id),
            title: files.find(
              (file) => file.file.id == sourceDocument.file_id,
            ) ? (
              translations.extractDocs.splitView.sourcesTab.tabTitle({
                count: String(index + 1),
              })
            ) : (
              <div className="flex items-center">
                <DotIcon className="text-warning" />
                <span>
                  {translations.extractDocs.splitView.sourcesTab.tabTitle({
                    count: String(index + 1),
                  })}
                </span>
              </div>
            ),
          }))}
          variant="bordered"
          onSelectionChange={(key) => onSelectionChange(key.toString())}
        />
        <div className="w-10" />
      </div>
      <div className="flex flex-col">
        <h3 className="text-heading-sm font-bold">
          {selectedFile.isDeleted
            ? translations.extractDocs.splitView.sourceInformation
                .deletedFileName
            : selectedFile.fileName}
        </h3>
        {!!selectedFile.uploadedAt && (
          <p className="text-body-md text-gray-600">
            {translations.extractDocs.splitView.sourcesTab.uploadedFileDateDetail(
              {
                date: formatUnixDate(selectedFile.uploadedAt, locale),
              },
            )}
          </p>
        )}
      </div>
    </div>
  );
};

const ExtractionStatusChip = ({ file }: { file: FileState }) => {
  const translations = useTranslations();
  const { utils } = useExtractDocs();

  const extractionStatus: (ChipProps & { label: string }) | null =
    useMemo(() => {
      if (utils.isFileExtractionFailed(file.id)) {
        return {
          color: "danger",
          id: "extraction-failed-chip",
          label: translations.extractDocs.extractBanner.extractionError,
          startContent: <AlertTriangleIcon size={16} />,
        };
      }
      if (utils.isFileExtractionProcessing(file.id)) {
        return {
          color: "primary",
          id: "extraction-processing-chip",
          label: translations.extractDocs.filesTabChanges.buttonExtracting,
          startContent: (
            <Spinner
              color="primary"
              size="xs"
              testId="spinner-extraction-processing"
            />
          ),
        };
      }
      if (utils.isFileExtracted(file.id)) {
        return {
          color: "success",
          id: "extracted-chip",
          label: translations.extractDocs.filesTabChanges.buttonExtracted,
          startContent: <CheckIcon size={16} />,
        };
      }
      return null;
    }, [
      file.id,
      translations.extractDocs.extractBanner.extractionError,
      translations.extractDocs.filesTabChanges.buttonExtracting,
      translations.extractDocs.filesTabChanges.buttonExtracted,
      utils,
    ]);

  if (!extractionStatus) {
    return null;
  }

  return (
    <Chip
      color={extractionStatus.color}
      id={extractionStatus.id}
      size="sm"
      startContent={extractionStatus.startContent}
      variant="flat"
    >
      {extractionStatus.label}
    </Chip>
  );
};

const SourceContent = ({
  externalId,
  files,
  resetSelectedSource,
  sourceDocuments,
}: {
  resetSelectedSource: () => void;
  files: { name: string; file: FileState }[];
  externalId: string;
  sourceDocuments?: { file_id: FileState["id"] }[];
}) => {
  const translations = useTranslations();
  const locale = useLocale();
  const [createFileUrlProgress, setCreateFileUrlProgress] =
    useState<QueryProgress>(QUERY_PROGRESS_NOT_STARTED);
  const urlCache = useRef<Map<FileState["id"], string>>(new Map());
  const [selectedFile, setSelectedFile] = useState<PdfViewerFile | undefined>();
  const [loadingFileId, setLoadingFileId] = useState<
    FileState["id"] | undefined
  >();
  const { utils } = useExtractDocs();

  const createFileUrl = useCreateFileUrl({
    externalId,
    onComplete: () => setLoadingFileId(undefined),
    setProgress: setCreateFileUrlProgress,
  });

  const handleSelectionChange = async ({
    fileId,
    files,
  }: {
    fileId: string;
    files: { name: string; file: FileState }[];
  }) => {
    const file = files.find((file) => String(file.file.id) === fileId);
    if (!file) {
      setSelectedFile({
        fileName: "",
        id: fileId,
        isDeleted: true,
        uploadedAt: undefined,
        url: "",
      });
      setLoadingFileId(undefined);
      return;
    }

    const cachedUrl = urlCache.current.get(fileId);
    if (cachedUrl) {
      setSelectedFile({
        fileName: file.name,
        id: fileId,
        uploadedAt: file.file.updated_at!,
        url: cachedUrl,
      });
      setLoadingFileId(undefined);
      return;
    }

    const extractedDocument = utils.getExtractedDocument(fileId);
    if (extractedDocument?.source_document_uri) {
      setSelectedFile({
        fileName: file.name,
        id: fileId,
        uploadedAt: file.file.updated_at!,
        url: extractedDocument.source_document_uri,
      });
      setLoadingFileId(undefined);
      return;
    }

    const url = await createFileUrl(file.file);
    if (url) {
      urlCache.current.set(fileId, url);
      setSelectedFile({
        fileName: file.name,
        id: fileId,
        uploadedAt: file.file.updated_at!,
        url,
      });
    }
  };

  useEffect(() => {
    if (sourceDocuments?.length) {
      handleSelectionChange({
        fileId: String(sourceDocuments[0].file_id),
        files,
      });
    }
  }, [sourceDocuments]);

  if (selectedFile) {
    return (
      <div className="flex h-full w-full flex-col items-center gap-4 overflow-y-auto">
        <Header
          files={files}
          selectedFile={selectedFile}
          sourceDocuments={sourceDocuments}
          onBack={() => {
            setSelectedFile(undefined);
            resetSelectedSource();
          }}
          onSelectionChange={(fileId: string) =>
            handleSelectionChange({
              fileId,
              files,
            })
          }
        />
        {selectedFile?.isDeleted ? (
          <>
            <Banner
              color="warning"
              message={
                translations.extractDocs.splitView.sourceInformation
                  .sourceDeletedTooltip
              }
            />
            <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50">
              <Image
                aria-hidden="true"
                className="h-[160px] w-auto overflow-visible"
                src={getStaticAsset("docs-deleted-source.svg")}
              />
              <p className="w-[50%] text-center text-heading-md text-gray-600">
                {
                  translations.extractDocs.splitView.sourceInformation
                    .sourceDeletedDescription
                }
              </p>
            </div>
          </>
        ) : (
          <PdfViewer selectedFile={selectedFile} />
        )}
      </div>
    );
  }

  if (!files?.length) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <Image
          alt={translations.extractDocs.splitView.sourcesTab.altTextEmptyState}
          className="h-[160px] w-auto overflow-visible"
          src={getStaticAsset("docs-empty-state.svg")}
        />
        <p className="w-[50%] text-center text-heading-md text-gray-600">
          {translations.extractDocs.splitView.sourcesTab.emptyStateInformation}
        </p>
      </div>
    );
  }

  return (
    <ul className="flex h-fit w-full flex-col gap-4">
      {files.map(({ file, name }) => {
        const isFileLoading =
          isLoading(createFileUrlProgress) && loadingFileId === file.id;

        return (
          <li key={`${name}-${file.updated_at}-${file.id}`} className="w-full">
            <Card
              aria-label={translations.extractDocs.splitView.sourcesTab.ariaLabelFileCard(
                { fileName: name },
              )}
              as="button"
              className="flex w-full flex-row rounded-lg border border-gray-300 bg-white"
              id={`source-card-${file.id}`}
              isLoading={isFileLoading}
              shadow="none"
              trackEventProps={{
                file_id: String(file.id),
                id: "source-card",
              }}
              onPress={() => {
                setLoadingFileId(file.id);
                handleSelectionChange({
                  fileId: String(file.id),
                  files,
                });
              }}
            >
              <CardBody className="flex w-full flex-row items-center justify-start gap-4 px-6 py-4">
                {isFileLoading ? (
                  <div className="flex items-center justify-center">
                    <Spinner
                      color="primary"
                      size="sm"
                      testId="spinner-loading-sources"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <FileTextIcon className="h-6 w-6 text-primary" />
                  </div>
                )}
                <div className="flex flex-1 flex-col gap-1 text-left">
                  <span className="break-all text-body-md font-bold text-gray-900">
                    {name}
                  </span>
                  <span className="text-body-sm text-gray-500">
                    {formatUnixDate(file.updated_at!, locale)}
                  </span>
                </div>
                <div className="flex items-center">
                  <ExtractionStatusChip file={file} />
                </div>
              </CardBody>
            </Card>
          </li>
        );
      })}
    </ul>
  );
};

export const DocsModalSources = ({
  files,
  patient,
  resetSelectedSource,
  sourceDocuments,
}: {
  sourceDocuments: { file_id: FileState["id"] }[];
  patient: Patient;
  resetSelectedSource: () => void;
  files: { name: string; file: FileState }[];
}) => {
  const translations = useTranslations();

  return (
    <div className="flex w-[50%] flex-col border-l border-gray-400">
      <div className="sticky top-0 w-full p-4">
        <h2 className="text-heading-lg font-bold">
          {translations.extractDocs.splitView.sourcesTab.title}
        </h2>
      </div>
      <hr className="w-full border-gray-400" />
      <div
        className={clsx("flex h-full w-full overflow-y-auto p-4", {
          "bg-gray-50": !files.length,
        })}
      >
        <SourceContent
          externalId={patient.external_id!}
          files={files}
          resetSelectedSource={resetSelectedSource}
          sourceDocuments={sourceDocuments}
        />
      </div>
    </div>
  );
};
